/**
 * <PlatformAdminGuard> — the management-console route-guard. Mirrors `<ProtectedRoute>` but gates on the
 * super-admin plane instead of a per-lab capability list (the `est_admin_plateforme` authority — see
 * ../session.ts):
 *   - unauthenticated  → redirect to /login (preserving the intended destination);
 *   - authenticated but not a platform super-admin → <ForbiddenState> (NOT a redirect — they are logged
 *     in, they simply may not see the cross-lab plane);
 *   - platform super-admin → render the console.
 *
 * The module wraps each management route's `element` in this guard (index.tsx). The standard router
 * `wrapFeatureRoutes` ALSO wraps every element in <ProtectedRoute> using the module's empty
 * `requiredCapabilities` (a no-op), so THIS guard is the real gate.
 */
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ForbiddenState } from "@/ui";
import { useAuthStore } from "@/auth";
import { ALL_MANAGEMENT_CAPS } from "../caps";
import { useIsPlatformAdmin } from "../session";

interface PlatformAdminGuardProps {
  children: ReactNode;
}

export function PlatformAdminGuard({ children }: PlatformAdminGuardProps) {
  const location = useLocation();
  const status = useAuthStore((s) => s.status);
  const allowed = useIsPlatformAdmin();

  if (status !== "authenticated") {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (!allowed) {
    return (
      <ForbiddenState
        requiredCapabilities={ALL_MANAGEMENT_CAPS}
        message="Cette console est réservée aux administrateurs de la plateforme Pharmakon."
      />
    );
  }
  return <>{children}</>;
}
